import { Link } from "react-router-dom";
import { BarChart3, Trophy, User as UserIcon, TrendingUp } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { reportApi } from "@/lib/extensions-api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatsCard } from "@/features/customer/components/StatsCard";

export function ReportsHubPage() {
  const today = useQuery({
    queryKey: ["admin-report-overview-today"],
    queryFn: () => reportApi.overview({ period: "today" }),
  });
  const month = useQuery({
    queryKey: ["admin-report-overview-monthly"],
    queryFn: () => reportApi.overview({ period: "monthly" }),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Reports</h1>
        <p className="text-sm text-surface-500 mt-1">
          Sales analytics, top-performer discovery, and downloadable PDFs.
        </p>
      </header>

      {today.isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatsCard icon={TrendingUp} tone="success" label="Today's Sales"
                      value={formatCurrency(today.data?.total_sales ?? "0")}
                      hint={`${today.data?.transaction_count ?? 0} transactions`} />
          <StatsCard icon={UserIcon}    tone="info"    label="Today's Customers"
                      value={formatNumber(today.data?.unique_customers ?? 0)} />
          <StatsCard icon={TrendingUp} tone="warning" label="Month-to-Date Sales"
                      value={formatCurrency(month.data?.total_sales ?? "0")} />
          <StatsCard icon={BarChart3}   tone="neutral" label="Month-to-Date Txns"
                      value={formatNumber(month.data?.transaction_count ?? 0)} />
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <ReportLink to="/admin/reports/sales"       icon={BarChart3} title="Sales Report"
                     desc="Total sales, transactions, and daily breakdowns." />
        <ReportLink to="/admin/reports/top-users"   icon={Trophy}    title="Top Users"
                     desc="Ranked list of best-performing customers." />
        <ReportLink to="/admin/reports/user-perf"   icon={UserIcon}  title="User Performance"
                     desc="Deep-dive into an individual customer's activity." />
      </div>
    </div>
  );
}

function ReportLink({ to, icon: Icon, title, desc }: {
  to: string; icon: any; title: string; desc: string;
}) {
  return (
    <Link to={to} className="card-elevated p-5 hover:shadow-elevate-2 transition-all group">
      <div className="w-11 h-11 rounded-lg bg-brand-100 text-brand-700 grid place-items-center group-hover:scale-110 transition-transform">
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-3 text-base font-semibold text-surface-900">{title}</div>
      <div className="mt-0.5 text-sm text-surface-500">{desc}</div>
    </Link>
  );
}
